import React, { useState, useEffect } from 'react';
import { Star, CheckCircle2, BarChart2, Sparkles, HelpCircle, Flame } from 'lucide-react';
import { DifficultyRatingService, DifficultyRatingInfo, DifficultyMeta } from '../services/difficultyRatingService';

interface DifficultyRatingWidgetProps {
  itemId: string;
  itemTitle?: string;
  isExam?: boolean;
  userId?: string;
  onRatingSubmitted?: (rating: number, info: DifficultyRatingInfo) => void;
}

const RATING_OPTIONS = [
  { value: 1, label: 'Very Easy', emoji: '🟢', color: 'hover:border-emerald-500 hover:bg-emerald-50 dark:hover:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400' },
  { value: 2, label: 'Easy', emoji: '🔵', color: 'hover:border-teal-500 hover:bg-teal-50 dark:hover:bg-teal-950/40 text-teal-600 dark:text-teal-400' },
  { value: 3, label: 'Medium', emoji: '🟡', color: 'hover:border-amber-500 hover:bg-amber-50 dark:hover:bg-amber-950/40 text-amber-600 dark:text-amber-400' },
  { value: 4, label: 'Hard', emoji: '🟠', color: 'hover:border-orange-500 hover:bg-orange-50 dark:hover:bg-orange-950/40 text-orange-600 dark:text-orange-400' },
  { value: 5, label: 'Very Hard', emoji: '🔴', color: 'hover:border-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/40 text-rose-600 dark:text-rose-400' },
];

export const DifficultyRatingWidget: React.FC<DifficultyRatingWidgetProps> = ({
  itemId,
  itemTitle = 'this practice session',
  isExam = false,
  userId = 'guest',
  onRatingSubmitted,
}) => {
  const [ratingInfo, setRatingInfo] = useState<DifficultyRatingInfo>(() =>
    DifficultyRatingService.getRating(itemId, isExam)
  );
  const [hoveredStar, setHoveredStar] = useState<number | null>(null);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [submittedRating, setSubmittedRating] = useState<number | null>(ratingInfo.userRating || null);

  useEffect(() => {
    const info = DifficultyRatingService.getRating(itemId, isExam);
    setRatingInfo(info);
    if (info.userRating) {
      setSubmittedRating(info.userRating);
      setIsSubmitted(true);
    }
  }, [itemId, isExam]);

  const handleSelectRating = async (ratingVal: number) => {
    setSubmittedRating(ratingVal);
    setIsSubmitted(true);
    const updated = await DifficultyRatingService.submitRating(itemId, ratingVal, isExam, userId);
    setRatingInfo(updated);
    if (onRatingSubmitted) {
      onRatingSubmitted(ratingVal, updated);
    }
  };

  const meta: DifficultyMeta = DifficultyRatingService.getMeta(ratingInfo.avgRating);
  const activeRating = hoveredStar || submittedRating || 0;
  const activeMeta = activeRating > 0 ? DifficultyRatingService.getMeta(activeRating) : null;

  return (
    <div id="difficulty-rating-widget" className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-[2rem] p-6 md:p-8 shadow-lg relative overflow-hidden transition-all my-6">
      <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/5 dark:bg-indigo-500/10 rounded-full blur-2xl pointer-events-none"></div>

      <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
        {/* Header Title & Description */}
        <div className="space-y-2 max-w-lg">
          <div className="flex items-center gap-2">
            <span className="px-3 py-1 rounded-full text-[9px] font-black tracking-widest uppercase bg-indigo-50 dark:bg-indigo-950/50 text-indigo-600 dark:text-indigo-400 border border-indigo-100 dark:border-indigo-900/40 flex items-center gap-1">
              <Sparkles size={11} /> Post-Exam Feedback
            </span>
            <span className={`px-2.5 py-0.5 rounded-full text-[8.5px] font-black tracking-widest uppercase border ${meta.badgeClass}`}>
              Avg Diff: {ratingInfo.avgRating.toFixed(1)} / 5 ({meta.label})
            </span>
          </div>

          <h3 className="text-base md:text-lg font-black text-slate-900 dark:text-white tracking-tight flex items-center gap-2">
            <Flame size={18} className="text-amber-500" />
            Rate Quiz Difficulty
          </h3>
          <p className="text-xs text-slate-500 dark:text-slate-400 font-medium leading-relaxed">
            How challenging did you find <strong className="text-slate-900 dark:text-slate-200">{itemTitle}</strong>? Your rating helps other students choose appropriate study tests.
          </p>
        </div>

        {/* Rating Input Area */}
        <div className="flex flex-col items-center md:items-end gap-3 bg-slate-50 dark:bg-slate-950/60 p-4 rounded-2xl border border-slate-100 dark:border-slate-800 shrink-0">
          <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
            {isSubmitted && submittedRating ? 'Your Rating Recorded:' : 'Select Difficulty Rating:'}
          </span>

          {/* Interactive 1-5 Star & Pill Buttons */}
          <div className="flex items-center gap-1.5 sm:gap-2">
            {RATING_OPTIONS.map((opt) => {
              const isSelected = submittedRating === opt.value;
              const isHovered = hoveredStar === opt.value;
              const isFilled = (hoveredStar !== null ? opt.value <= hoveredStar : (submittedRating !== null && opt.value <= submittedRating));

              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => handleSelectRating(opt.value)}
                  onMouseEnter={() => setHoveredStar(opt.value)}
                  onMouseLeave={() => setHoveredStar(null)}
                  className={`flex flex-col items-center justify-center p-2.5 rounded-xl border-2 transition-all duration-200 active:scale-95 group min-w-[50px] sm:min-w-[58px] ${
                    isSelected
                      ? 'bg-slate-900 dark:bg-white text-white dark:text-slate-900 border-slate-900 dark:border-white shadow-md scale-105'
                      : isFilled
                      ? 'border-amber-400 bg-amber-50/50 dark:bg-amber-950/30 text-amber-600 dark:text-amber-400'
                      : 'border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 ' + opt.color
                  }`}
                  title={`${opt.value}/5 - ${opt.label}`}
                >
                  <Star
                    size={20}
                    className={`transition-transform duration-200 group-hover:scale-110 ${
                      isFilled || isSelected
                        ? 'fill-amber-400 text-amber-400'
                        : 'text-slate-300 dark:text-slate-600'
                    }`}
                  />
                  <span className="text-[9px] font-black mt-1 leading-none tabular-nums">
                    {opt.value}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Active selection feedback text */}
          <div className="text-center md:text-right h-5">
            {activeMeta ? (
              <span className={`text-xs font-black transition-all animate-fade-in ${activeMeta.textClass}`}>
                {activeRating}/5 — {activeMeta.label} ({activeMeta.description})
              </span>
            ) : (
              <span className="text-[10px] font-bold text-slate-400">
                Click a star (1 = Very Easy, 5 = Very Hard)
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Confirmation & Community Rating Footer */}
      <div className="mt-5 pt-4 border-t border-slate-100 dark:border-slate-800/80 flex flex-wrap items-center justify-between gap-3 text-xs">
        <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300 font-bold">
          <BarChart2 size={15} className="text-indigo-500" />
          <span>
            Community Rating Average: <strong className="text-slate-900 dark:text-white font-black">{ratingInfo.avgRating.toFixed(1)} / 5.0</strong>
          </span>
          <span className="text-slate-400 text-[11px] font-normal">
            ({ratingInfo.count} student ratings)
          </span>
        </div>

        {isSubmitted && (
          <div className="flex items-center gap-1.5 px-3 py-1 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800 rounded-full font-black text-[10px] uppercase tracking-wider animate-pop">
            <CheckCircle2 size={13} className="text-emerald-500" />
            Rating Recorded ({submittedRating}/5) - Thank you!
          </div>
        )}
      </div>
    </div>
  );
};
